import {
  getCountryKeyByCode,
  getCountryNameFromCode,
} from "./countryLabels";

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN_X = 54;
const MARGIN_TOP = 54;
const MARGIN_BOTTOM = 54;

const PDF_STYLES = {
  title: {
    font: "F2",
    size: 18,
    lineHeight: 26,
    indent: 0,
    spacingBefore: 0,
  },
  heading: {
    font: "F2",
    size: 13,
    lineHeight: 18,
    indent: 0,
    spacingBefore: 10,
  },
  item: {
    font: "F1",
    size: 10,
    lineHeight: 14,
    indent: 14,
    spacingBefore: 0,
  },
};

const normalizeText = (value) =>
  String(value ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u2022/g, "-")
    .replace(/\s+/g, " ")
    .replace(/[^\x20-\x7E]/g, "?")
    .trim();

const escapePdfText = (value) =>
  normalizeText(value)
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");

const asArray = (value) => (Array.isArray(value) ? value : []);

const presentValue = (value, fallback = "0") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  return normalizeText(value);
};

const formatCount = (value) => presentValue(value, "0");

const formatListItem = (item) => {
  if (item === null || item === undefined) {
    return "";
  }

  if (typeof item !== "object") {
    return item;
  }

  const label =
    item.name ?? item.title ?? item.category ?? item.label ?? item.value ?? "";

  if (label && item.count !== null && item.count !== undefined) {
    return `${label}: ${item.count}`;
  }

  return label;
};

const listOrEmpty = (items) => {
  const normalizedItems = items
    .map((item) => normalizeText(formatListItem(item)))
    .filter(Boolean);

  return normalizedItems.length ? normalizedItems : ["No data to display"];
};

const formatCountries = (heatMapData) => {
  const countries = asArray(heatMapData)
    .map((item) => {
      const countryKey = getCountryKeyByCode(item?.country);

      if (!countryKey) {
        return null;
      }

      const countryName =
        getCountryNameFromCode(item?.country) ||
        normalizeText(item?.country) ||
        "Unknown Country";

      return {
        name: countryName,
        count: Number(item?.value) || 0,
      };
    })
    .filter(Boolean)
    .filter((item) => item.name || item.count)
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .map((item) => `${item.name}: ${item.count}`);

  return listOrEmpty(countries);
};

const formatReferralLinks = (links) => {
  const formattedLinks = asArray(links).map((item) => {
    const label =
      typeof item === "object" && item !== null
        ? normalizeText(item.name) || normalizeText(item.link) || "Unknown link"
        : normalizeText(item) || "Unknown link";
    const count = typeof item === "object" && item !== null ? item.count : 0;

    return `${label}: ${formatCount(count)}`;
  });

  return listOrEmpty(formattedLinks);
};

export const buildDashboardReportSections = ({
  businessName,
  reportFilters,
  overviewData,
  topCategories,
  trends,
  flaggedConcerns,
}) => {
  const startDate = presentValue(reportFilters?.startDate, "Not selected");
  const endDate = presentValue(reportFilters?.endDate, "Not selected");

  return [
    {
      heading: "Business name",
      items: [presentValue(businessName, "Business not selected")],
    },
    {
      heading: "Report dates",
      items: [`${startDate} - ${endDate}`],
    },
    {
      heading: "Total questions answered",
      items: [presentValue(overviewData?.totalMessage)],
    },
    {
      heading: "Number of Guest Conversations",
      items: [presentValue(overviewData?.totalThreadCount)],
    },
    {
      heading: "Staffing Hours Saved",
      items: [presentValue(overviewData?.staffingHoursSaved)],
    },
    {
      heading: "Countries",
      items: formatCountries(overviewData?.heatMap),
    },
    {
      heading: "Top 3 Categories",
      items: listOrEmpty(asArray(topCategories)),
    },
    {
      heading: "Referral Links and Qtys",
      items: formatReferralLinks(overviewData?.clickedLinkList),
    },
    {
      heading: "Trends",
      items: listOrEmpty(asArray(trends)),
    },
    {
      heading: "Flagged Concerns",
      items: listOrEmpty(asArray(flaggedConcerns)),
    },
  ];
};

const getMaxChars = (style) => {
  const availableWidth = PAGE_WIDTH - MARGIN_X * 2 - style.indent;
  return Math.max(24, Math.floor(availableWidth / (style.size * 0.52)));
};

const wrapText = (text, maxChars) => {
  const words = normalizeText(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    if (word.length > maxChars) {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = "";
      }

      for (let index = 0; index < word.length; index += maxChars) {
        lines.push(word.slice(index, index + maxChars));
      }
      return;
    }

    const nextLine = currentLine ? `${currentLine} ${word}` : word;

    if (nextLine.length > maxChars) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = nextLine;
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines.length ? lines : [""];
};

const paginateLines = (sections) => {
  const pages = [[]];
  let y = PAGE_HEIGHT - MARGIN_TOP;

  const getLineHeight = (styleName, firstWrappedLine = true) => {
    const style = PDF_STYLES[styleName];
    const spacingBefore = firstWrappedLine ? style.spacingBefore : 0;

    return spacingBefore + style.lineHeight;
  };

  const startNewPage = () => {
    pages.push([]);
    y = PAGE_HEIGHT - MARGIN_TOP;
  };

  const ensureBlockFits = (lines) => {
    const availableHeight = PAGE_HEIGHT - MARGIN_TOP - MARGIN_BOTTOM;
    const blockHeight = lines.reduce(
      (total, line) =>
        total + getLineHeight(line.styleName, line.firstWrappedLine),
      0
    );

    if (blockHeight <= availableHeight && y - blockHeight < MARGIN_BOTTOM) {
      startNewPage();
    }
  };

  const addLine = (styleName, text, firstWrappedLine = true) => {
    const style = PDF_STYLES[styleName];
    const spacingBefore = firstWrappedLine ? style.spacingBefore : 0;

    if (y - getLineHeight(styleName, firstWrappedLine) < MARGIN_BOTTOM) {
      startNewPage();
    }

    y -= spacingBefore;
    pages[pages.length - 1].push({
      styleName,
      text,
      x: MARGIN_X + style.indent,
      y,
    });
    y -= style.lineHeight;
  };

  addLine("title", "Dashboard Report");

  sections.forEach((section) => {
    const items = listOrEmpty(section.items);
    const firstItemLines = wrapText(
      `- ${items[0]}`,
      getMaxChars(PDF_STYLES.item)
    ).map((line, index) => ({
      styleName: "item",
      text: line,
      firstWrappedLine: index === 0,
    }));

    ensureBlockFits([
      {
        styleName: "heading",
        text: section.heading,
        firstWrappedLine: true,
      },
      ...firstItemLines,
    ]);
    addLine("heading", section.heading);

    items.forEach((item) => {
      const wrappedLines = wrapText(`- ${item}`, getMaxChars(PDF_STYLES.item));
      wrappedLines.forEach((line, index) => {
        addLine("item", line, index === 0);
      });
    });
  });

  return pages;
};

const buildPageContent = (pageLines) =>
  pageLines
    .map((line) => {
      const style = PDF_STYLES[line.styleName];

      return `BT /${style.font} ${style.size} Tf ${line.x} ${line.y} Td (${escapePdfText(
        line.text
      )}) Tj ET`;
    })
    .join("\n");

const padOffset = (value) => String(value).padStart(10, "0");

const buildPdf = (pageContents) => {
  const objects = [];
  const pageObjectIds = [];

  objects[1] = "<< /Type /Catalog /Pages 2 0 R >>";
  objects[3] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>";
  objects[4] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>";

  pageContents.forEach((content, index) => {
    const pageObjectId = 5 + index * 2;
    const contentObjectId = pageObjectId + 1;

    pageObjectIds.push(`${pageObjectId} 0 R`);
    objects[pageObjectId] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${contentObjectId} 0 R >>`;
    objects[contentObjectId] = `<< /Length ${content.length} >>\nstream\n${content}\nendstream`;
  });

  objects[2] = `<< /Type /Pages /Kids [${pageObjectIds.join(
    " "
  )}] /Count ${pageObjectIds.length} >>`;

  let pdf = "%PDF-1.4\n";
  const offsets = [0];

  for (let objectId = 1; objectId < objects.length; objectId += 1) {
    offsets[objectId] = pdf.length;
    pdf += `${objectId} 0 obj\n${objects[objectId]}\nendobj\n`;
  }

  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length}\n`;
  pdf += "0000000000 65535 f \n";

  for (let objectId = 1; objectId < objects.length; objectId += 1) {
    pdf += `${padOffset(offsets[objectId])} 00000 n \n`;
  }

  pdf += `trailer\n<< /Size ${objects.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return pdf;
};

export const createDashboardReportPdf = (sections) =>
  buildPdf(paginateLines(sections).map(buildPageContent));

export const downloadDashboardReportPdf = (reportData, fileName) => {
  const sections = buildDashboardReportSections(reportData);
  const pdf = createDashboardReportPdf(sections);
  const blob = new Blob([pdf], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 0);
};
